import React, { useMemo } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AlertColor } from '@mui/material';

import type { definitions } from '@luxuryescapes/contract-svc-promo';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherLogsTable from '~/components/Vouchers/VoucherLogsTable';
import VoucherRecentRequests from '~/components/Vouchers/VoucherRecentRequests';

import {
  type PromoMeta,
  type Voucher,
  type VoucherAutoAssignmentRule,
  getVoucherWarnings,
} from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

type VoucherCondition = definitions['VoucherConditionV1'];

function voucherFormPagePath(voucherId: string) {
  return `/marketing/vouchers/${voucherId}`;
}

function voucherRulePagePath(ruleId: string) {
  return `/marketing/voucher-auto-assign-rules/${ruleId}`;
}

function getVoucherWarningSeverity(level?: string): AlertColor {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
}

type Props = {
  rule: VoucherAutoAssignmentRule;
  voucher?: Voucher;
  brand: string;
  promoMeta?: PromoMeta | null;
};

export default function VoucherAutoAssignRuleGroupRuleAccordion({ rule, voucher, brand, promoMeta }: Props) {
  const warnings = useMemo(() => (voucher ? getVoucherWarnings(voucher) : []), [voucher]);
  const conditions = useMemo<Array<VoucherCondition>>(() => rule.ruleData.rule_conditions ?? [], [rule.ruleData]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 0.5, md: 2 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ minWidth: 0, width: '100%' }}
        >
          <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2">{voucher?.voucherName ?? rule.voucherId}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {rule.voucherAutoAssignmentRuleId}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Apply order ${rule.applyOrder ?? '-'}`} />
            <Chip
              size="small"
              label={rule.isActive ? 'Active' : 'Inactive'}
              color={rule.isActive ? 'success' : 'default'}
            />
            <Chip size="small" label={rule.regions?.join(', ') || 'No regions'} variant="outlined" />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <PageSubheader title="Rule" />
              <Button
                component={RouterLink}
                to={voucherRulePagePath(rule.voucherAutoAssignmentRuleId)}
                variant="text"
                color="primary"
              >
                Edit rule
              </Button>
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
              <FormControlLabel control={<Checkbox checked={rule.isActive} disabled />} label="Rule active" />
              <TextField label="Rule type" value={rule.ruleType} disabled sx={{ minWidth: 260, flex: '1 1 260px' }} />
              <TextField label="Brand" value={rule.brand} disabled sx={{ minWidth: 180 }} />
              <TextField
                label="Assignment group"
                value={rule.assignmentGroup ?? ''}
                disabled
                sx={{ minWidth: 260, flex: '1 1 260px' }}
              />
              <TextField label="Apply order" value={rule.applyOrder ?? ''} disabled sx={{ minWidth: 160 }} />
              <TextField label="Regions" value={rule.regions?.join(', ') ?? ''} disabled sx={{ minWidth: 260 }} />
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Start assignment
                </Typography>
                <DateFormatterDetailed date={rule.startAssignmentDate} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  End assignment
                </Typography>
                <DateFormatterDetailed date={rule.endAssignmentDate} />
              </Box>
            </Stack>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Rule conditions
              </Typography>
              <VoucherConditions
                conditions={conditions}
                rewards={[]}
                onChange={() => undefined}
                disabled
                includeRewardIdFilters={false}
                allowedConditionTypeKeys={
                  promoMeta?.voucher_auto_assignment_rule_options?.rule_condition_types_supported?.map((c) => c.key) ??
                  undefined
                }
                additionalVoucherConditionTypeConfigs={
                  promoMeta?.voucher_auto_assignment_rule_options?.rule_condition_types_supported
                }
              />
            </Box>
          </Box>

          <Box>
            <PageSubheader title="Linked voucher" />
            {voucher ? (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {warnings.length > 0 ? (
                  <Stack spacing={0.75}>
                    {warnings.map((warning, index) => (
                      <Alert
                        key={`${warning.code ?? 'voucher-warning'}-${index}`}
                        severity={getVoucherWarningSeverity(warning.level)}
                        variant="outlined"
                        sx={{
                          py: 0.25,
                          '& .MuiAlert-message': { py: 0.5, width: '100%' },
                          '& .MuiAlert-icon': { py: 0.5, alignItems: 'flex-start' },
                        }}
                      >
                        <Typography variant="caption" component="span" fontWeight={600} display="block">
                          {warning.code ?? 'Voucher warning'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span" display="block">
                          {warning.message ?? 'Review this voucher before relying on this rule.'}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                ) : null}
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <TextField
                    label="Name"
                    value={voucher.voucherName}
                    disabled
                    sx={{ minWidth: 280, flex: '1 1 280px' }}
                  />
                  <TextField
                    label="Title"
                    value={voucher.voucherTitle ?? ''}
                    disabled
                    sx={{ minWidth: 280, flex: '1 1 280px' }}
                  />
                  <TextField
                    label="Voucher ID"
                    value={voucher.voucherId}
                    disabled
                    sx={{ minWidth: 280, flex: '1 1 280px', '& input': { fontFamily: 'monospace' } }}
                  />
                  <TextField label="Product type" value={voucher.productType ?? ''} disabled sx={{ minWidth: 220 }} />
                  <TextField label="Theme" value={voucher.theme ?? ''} disabled sx={{ minWidth: 220 }} />
                </Stack>
                {voucher.voucherDescription ? (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {voucher.voucherDescription}
                  </Typography>
                ) : null}
                {voucher.voucherToggles?.length ? (
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                    {voucher.voucherToggles.map((toggle) => (
                      <Chip key={toggle} size="small" label={toggle} variant="outlined" />
                    ))}
                  </Stack>
                ) : null}
                <Box>
                  <Link component={RouterLink} to={voucherFormPagePath(voucher.voucherId)} underline="hover">
                    Open voucher in admin
                  </Link>
                </Box>
                <VoucherRecentRequests
                  voucherName={voucher.voucherName}
                  userId=""
                  brand={brand}
                  promoMeta={promoMeta}
                  idPrefix={`voucher-auto-assign-group-${rule.voucherAutoAssignmentRuleId}`}
                  showVoucherLogsAccordion={false}
                />
                <VoucherLogsTable voucherId={voucher.voucherId} pageSizeOptions={[10, 20, 50]} flushTop />
              </Stack>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Voucher details could not be loaded for this rule.
              </Alert>
            )}
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
